'use client'

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabaseClient';

export default function ForgotPasswordPage() {
	const [email, setEmail] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);
		setError(null);

		try {
			const supabase = createClient();
			const origin = window.location.origin;

			const { error } = await supabase.auth.resetPasswordForEmail(email, {
				redirectTo: `${origin}/auth/confirm?type=recovery`,
			});

			if (error) {
				setError(error.message);
				setIsLoading(false);
			} else {
				setEmailSent(true);
				setIsLoading(false);
			}
		} catch (err) {
			setError('An unexpected error occurred. Please try again.');
			setIsLoading(false);
		}
	};

	if (emailSent) {
		return (
			<div className="flex flex-col justify-center items-center">
				<div className="w-full max-w-md px-8 space-y-4">
					<h1 className="text-2xl font-bold text-center">Check your email</h1>
					<p className="text-center text-muted-foreground">
						We've sent a password reset link to <strong>{email}</strong>
					</p>
					<p className="text-center text-sm text-muted-foreground">
						The link will expire in 1 hour. If you don't see the email, check your spam folder.
					</p>
					<div className="pt-4">
						<Link href="/auth/login" className="text-muted-foreground hover:text-foreground hover:underline block text-center">
							Back to login
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col justify-center items-center">
			<div className="w-full max-w-md px-8">
				<h1 className="text-2xl font-bold text-center mb-6">Reset your password</h1>
				<p className="text-center text-muted-foreground mb-6">
					Enter your email address and we'll send you a link to reset your password.
				</p>
				<form onSubmit={handleSubmit} className="space-y-4">
					<Input
						type="email"
						id="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="Email"
						required
					/>
					{error && <p className="text-destructive">{error}</p>}
					<Button className='w-full' type="submit" disabled={isLoading}>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Sending...
							</>
						) : (
							"Send Reset Link"
						)}
					</Button>
				</form>
				<p className="mt-4 text-center">
					<Link href="/auth/login" className="text-muted-foreground hover:text-foreground hover:underline">
						Back to login
					</Link>
				</p>
			</div>
		</div>
	);
}
